// Maschinencode-Ansicht: Befehlsbausteine in die Speicherzellen ziehen (oder Zelle antippen, dann
// Baustein antippen), dann der CPU zusehen: Holen → Entschlüsseln → Ausführen.
// Die Palette steht rechts neben dem Speicher, die Bausteine wandern nach links in die Zellen.
// Assembler-Aufgaben nutzen dieselbe Ansicht: rechts ein Texteditor statt der Palette, der Knopf
// „Assemblieren“ schreibt die übersetzten Zeilen als Bitmuster in den Speicher.
// Programm und Daten liegen im selben Speicher (Von Neumann), der Befehlszähler zeigt auf die Zelle.

import { reducedMotion } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { BEFEHLE, CODES, LEER, MAX_QUELLTEXT, MAX_SCHRITTE, ZELLEN, adr, anfang, assemble, bausteinOk, check, deute, schritt, speicherStart } from "./model.ts";
import type { AsmFehler, Befehl, Ereignis, Loesung, Result, Task, Zustand } from "./model.ts";
import { store } from "./store.ts";

const W = 1000;
const ROW_H = 36, MEM_TOP = 20, MEM_HEAD = 58;
const CPU_X0 = 146, CPU_X1 = 480;
const MEM_X0 = 570, MEM_X1 = W - 12;
const BOX_B = MEM_TOP + MEM_HEAD + ZELLEN * ROW_H + 12; // Unterkante von CPU, Speicher und Palette
const H = BOX_B + 12;
// Bus senkrecht zwischen CPU und Speicher; Abzweigung zur CPU im oberen, zum Speicher im unteren Drittel
const BUS_X = (CPU_X1 + MEM_X0) / 2;
const CPU_ABZ_Y = MEM_TOP + (BOX_B - MEM_TOP) / 3, MEM_ABZ_Y = MEM_TOP + 2 * (BOX_B - MEM_TOP) / 3;
const PAL_X = W + 34, PAL_W = 176, TILE_H = 70; // Lücke zum Speicher: Platz für den Ablage-Pfeil
const VX = CPU_X0 - 10, VX1 = PAL_X + PAL_W + 16; // sichtbarer Bereich (Assembler: ohne Palette bis W)
const ADR_END = 680, BITS_X = 692, BIT_W = 21, NIB_GAP = 6, DEUT_X = BITS_X + 8 * BIT_W + NIB_GAP + 10;
const BZ_Y = 110, BR_Y = 196, DEC_Y = 250, PHASE_Y = 300, REG_Y = 470;
const CPU_BTN_W = 44; // Schritt/Ablauf oben in der CPU
const CELL_X = CPU_X0 + 140; // Zellen von BZ, BR und Register
const BR_X1 = CELL_X + 8 * BIT_W + NIB_GAP;

type Phase = "" | "holen" | "entschluesseln" | "ausfuehren";
const PHASEN: [Phase, string][] = [["holen", "Holen"], ["entschluesseln", "Entschlüsseln"], ["ausfuehren", "Ausführen"]];

/** Bausteine der Palette: Symbol, deutscher Name, Erklärung.
 *  Im Maschinencode-Modus erscheinen Name und Kürzel nicht -- die CPU kennt nur Bitmuster,
 *  Namen für die Befehle kommen erst mit dem Assembler. */
const BAUSTEIN: Record<Befehl, { sym: string; name: string; wirkung: string }> = {
  LOAD: { sym: "⇐", name: "Laden", wirkung: "Register ← Zelle a" },
  STORE: { sym: "⇒", name: "Speichern", wirkung: "Zelle a ← Register" },
  INC: { sym: "+1", name: "Erhöhen", wirkung: "Register + 1" },
  SHL: { sym: "×2", name: "Verdoppeln", wirkung: "Bits nach links" },
  HALT: { sym: "■", name: "Stopp", wirkung: "Programm endet" },
};

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const wait = (ms: number) => new Promise(r => window.setTimeout(r, ms));
const rowY = (i: number) => MEM_TOP + MEM_HEAD + i * ROW_H;
const bitX = (k: number) => BITS_X + k * BIT_W + (k >= 4 ? NIB_GAP : 0);
const dez = (w: string) => parseInt(w, 2);

/** Weg eines Bitpakets von einer Speicherzelle (Höhe yZelle) über den Bus in die CPU bis (xCpu, yCpu). */
function busWeg(yZelle: number, yCpu: number, xCpu = (CELL_X + BR_X1) / 2): [number, number][] {
  return [[MEM_X0, yZelle], [MEM_X0, MEM_ABZ_Y], [BUS_X, MEM_ABZ_Y], [BUS_X, CPU_ABZ_Y], [CPU_X1, CPU_ABZ_Y], [CPU_X1, yCpu], [xCpu, yCpu]];
}

/** Text für das Protokoll: Was hat dieser Befehl bewirkt? */
export function beschreibe(e: Ereignis): string {
  switch (e.typ) {
    case "halt": return "Programm beendet";
    case "unbekannt": return `Befehlscode ${e.befehl.slice(0, 4)} unbekannt -- Abbruch`;
    case "load": return `Register ← Zelle ${dez(e.adresse)}: ${e.wert} (${dez(e.wert)})`;
    case "store": return `Zelle ${dez(e.adresse)} ← ${e.wert} (${dez(e.wert)})`;
    case "inc": case "shl":
      return `Register ${dez(e.vorher)} ${e.typ === "inc" ? "+ 1" : "× 2"} → ${e.wert} (${dez(e.wert)})`
        + (e.ueberlauf ? " -- Überlauf: die vorderste 1 passt nicht mehr ins Register" : "");
  }
}

/** Langtext für den Decoder im Steuerwerk; Maschinencode: Befehlscode statt Kürzel. */
function erklaere(w: string, asm: boolean): string {
  const b = BEFEHLE[w.slice(0, 4)];
  if (!b) return `${w.slice(0, 4)} = ? -- unbekannter Befehl`;
  const a = dez(w.slice(4));
  if (!asm) return `${w.slice(0, 4)}: ${BAUSTEIN[b.name].wirkung.replace(" a", ` ${a}`)}`;
  switch (b.name) {
    case "LOAD": return `LOAD ${a}: Register ← Zelle ${a}`;
    case "STORE": return `STORE ${a}: Zelle ${a} ← Register`;
    case "INC": return "INC: Register + 1";
    case "SHL": return "SHL: Register × 2";
    case "HALT": return "HALT: anhalten";
  }
}

interface Drag { quelle: { tile: string } | { row: number }; x0: number; y0: number; aktiv: boolean; ghost?: SVGGElement }

export class MaschineView {
  private programm: string[];
  private undoStack: string[][] = [];
  private sel = 0;                         // ausgewählte Zelle (Cursor), nächster Baustein landet hier
  private adresseOffen = false;            // in sel steht ein eben angetippter LOAD/STORE: Adresse fehlt noch
  private lauf: { zustand: Zustand; ereignisse: Ereignis[] } | null = null;
  private phase: Phase = "";
  private br: string | null = null;        // Befehlsregister
  private bzAnzeige: number | null = null; // BZ schon erhöht, Befehl noch nicht fertig
  private aluText = "";
  private busy = false;
  private ablauf = false;                  // „Ablauf“ läuft, bis HALT oder „Anhalten“
  private bzVonHand = false;               // BZ per Klick auf eine Adresse gesetzt: Lauf wird nicht bewertet
  private result: Result | null = null;
  private solvedShown = false;
  private hintsShown: number;
  private flash = new Set<string>();
  private note = "";
  private popStars = false;
  private drag: Drag | null = null;
  private tapZiel: Element | null = null; // pointerdown-Ziel: wegen setPointerCapture liefert pointerup nur das SVG
  private destroyed = false;
  // nur Assembler
  private quelltext = "";
  private assembliert = false;
  private asmFehler: AsmFehler[] = [];
  private asmZeile: number | null = null; // Zeile, die gerade übersetzt wird

  private readonly asm: boolean;
  private readonly start: string[];
  private readonly daten: Map<number, { wert: number; name: string }>;
  private readonly svg: SVGSVGElement;
  private readonly side: HTMLElement;
  private readonly flyLayer: SVGGElement;
  private readonly root: HTMLElement;
  private readonly task: Task;
  private readonly next: { id: string; titel: string } | null;
  private readonly tiles: string[];

  constructor(root: HTMLElement, task: Task, next: { id: string; titel: string } | null = null) {
    this.root = root;
    this.task = task;
    this.next = next;
    this.start = speicherStart(task);
    this.daten = new Map(task.daten.map(d => [d.zelle, d]));
    this.hintsShown = store.hints(task.id);
    this.tiles = [...task.befehle, "HALT" as Befehl].map(b => CODES[b] + "0000");
    this.asm = task.typ === "assembler";
    const saved = this.asm ? null : store.programm(task.id);
    this.programm = saved && this.gueltig(saved) ? saved : [...this.start];
    if (this.asm) this.quelltext = (store.quelltext(task.id) ?? task.vorlage ?? "").slice(0, MAX_QUELLTEXT);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench maschine${this.asm ? " asm" : ""}">
        <div class="canvas-wrap">
          <svg class="canvas" viewBox="${VX} 0 ${(this.asm ? W : VX1) - VX} ${H}" role="application"
               aria-label="Befehle, CPU und Speicher"><g class="layer"></g><g class="fly"></g></svg>
          <div class="toolbar">
            ${this.asm ? `<button data-act="neu" title="Quelltext auf den Anfang der Aufgabe zurücksetzen (Strg/⌘+Z im Editor nimmt das zurück)">Neu</button>` : ""}
            ${this.asm ? "" : `<button data-act="undo" title="Letzte Änderung am Programm zurücknehmen">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="leeren" title="Ausgewählte Zelle leeren">${ICON.trash}<span>Zelle leeren</span></button>
            <button data-act="clear" title="Ganzes Programm löschen">Neu</button>`}
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="reset" title="CPU zurücksetzen: Befehlszähler 0, Speicher wie programmiert">Zurücksetzen</button>
            ${this.asm ? `<button data-act="assemble" title="Quelltext in Maschinencode übersetzen">${ICON.gear}<span>Assemblieren</span></button>` : ""}
            <button data-act="schritt" title="Einen Befehl ausführen">${ICON.step}<span>Schritt</span></button>
            <button data-act="ablauf" class="primary"></button>
          </div>
        </div>
        ${this.asm ? `<div class="editor">
          <div class="editor-head"><div class="editor-titel"><strong>Assembler</strong>
            <button type="button" class="asm-btn" data-act="assemble" title="Assemblieren: Quelltext in Maschinencode übersetzen"
              aria-label="Assemblieren">${ICON.gear}</button></div><span>Zelle</span></div>
          <div class="editor-body"><pre class="gutter" aria-hidden="true"></pre><textarea
            spellcheck="false" autocapitalize="off" autocomplete="off" autocorrect="off" wrap="off"
            maxlength="${MAX_QUELLTEXT}" aria-label="Assembler-Quelltext, ein Befehl pro Zeile"></textarea></div>
          <ul class="asm-fehler" role="status"></ul>
        </div>` : ""}
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.flyLayer = this.svg.querySelector("g.fly")!;
    this.side = root.querySelector(".side")!;

    this.svg.addEventListener("pointerdown", e => this.down(e));
    this.svg.addEventListener("pointermove", e => this.move(e));
    this.svg.addEventListener("pointerup", e => this.up(e));
    this.svg.addEventListener("pointercancel", () => this.endDrag());
    // Scrollen per Touch nur verhindern, wenn pointerdown (kommt vor touchstart) ein Ziehen begonnen hat
    this.svg.addEventListener("touchstart", e => { if (this.drag) e.preventDefault(); }, { passive: false });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    if (this.asm) {
      const ta = root.querySelector<HTMLTextAreaElement>(".editor textarea")!;
      const gutter = root.querySelector<HTMLElement>(".editor .gutter")!;
      ta.value = this.quelltext;
      ta.addEventListener("input", () => this.textGeaendert(ta.value));
      ta.addEventListener("scroll", () => { gutter.scrollTop = ta.scrollTop; });
      // kleiner „Assemblieren“-Knopf direkt am Editor (der in der Werkzeugleiste ist weit unten)
      root.querySelector(".asm-btn")!.addEventListener("click", () => this.action("assemble"));
      ta.addEventListener("keydown", e => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.action("assemble"); }
      });
    }
    this.render();
  }

  destroy(): void {
    this.destroyed = true;
    this.ablauf = false;
  }

  // ---------------------------------------------------------------- Programm bearbeiten

  private gueltig(sp: string[]): boolean {
    return Array.isArray(sp) && sp.length === ZELLEN && sp.every((w, i) =>
      typeof w === "string" && (this.daten.has(i) ? w === this.start[i] : /^[01]{8}$/.test(w) && bausteinOk(this.task, w)));
  }

  private istDaten = (i: number) => this.daten.has(i);

  private naechsteProgrammzelle(i: number): number {
    for (let j = i + 1; j < ZELLEN; j++) if (!this.istDaten(j)) return j;
    return i;
  }

  /** Jede Änderung am Programm setzt die CPU zurück -- sonst passt der Ablauf nicht mehr zum Programm. */
  private aendern(f: (p: string[]) => void): void {
    if (this.busy) return;
    const vorher = [...this.programm];
    f(this.programm);
    if (this.programm.every((w, i) => w === vorher[i])) return;
    this.undoStack.push(vorher);
    if (this.undoStack.length > 100) this.undoStack.shift();
    store.saveProgramm(this.task.id, this.programm);
    this.zuruecksetzen();
  }

  private setzen(i: number, w: string): void {
    if (this.istDaten(i)) {
      this.note = `Zelle ${i} enthält Daten (${this.daten.get(i)!.name || "gesperrt"}) -- dort kann kein Befehl hin.`;
      this.render();
      return;
    }
    this.aendern(p => { p[i] = w; });
    this.flash.add(`row:${i}`);
    this.render();
  }

  private bitUmschalten(i: number, k: number): void {
    const w = this.programm[i];
    if (this.istDaten(i) || k < 4 || !BEFEHLE[w.slice(0, 4)]?.adresse) return;
    this.aendern(p => { p[i] = w.slice(0, k) + (w[k] === "1" ? "0" : "1") + w.slice(k + 1); });
    this.render();
  }

  // ---------------------------------------------------------------- Assembler

  /** Jede Textänderung macht die Übersetzung ungültig: Speicher wieder nur mit den Daten. */
  private textGeaendert(text: string): void {
    this.quelltext = text;
    store.saveQuelltext(this.task.id, text);
    if (this.assembliert || this.lauf || this.asmFehler.length) {
      this.assembliert = false;
      this.asmFehler = [];
      this.programm = [...this.start];
      this.zuruecksetzen();
      this.render();
    } else {
      this.renderEditor();
      this.renderToolbar(); // „Neu“ freigeben bzw. sperren
    }
  }

  /** Assembler: zurück zur Vorlage der Aufgabe. Über insertText, damit Strg/⌘+Z im Editor das zurücknimmt. */
  private neuerQuelltext(): void {
    const ta = this.root.querySelector<HTMLTextAreaElement>(".editor textarea")!;
    const vorlage = this.task.vorlage ?? "";
    ta.focus();
    ta.select();
    const ok = vorlage ? document.execCommand("insertText", false, vorlage) : document.execCommand("delete");
    if (!ok || ta.value !== vorlage) { ta.value = vorlage; this.textGeaendert(vorlage); } // ohne execCommand
    ta.setSelectionRange(0, 0);
    ta.scrollTop = 0;
    this.note = "";
    this.render();
  }

  private async assemblieren(): Promise<void> {
    const a = assemble(this.task, this.quelltext);
    this.zuruecksetzen();
    this.asmFehler = a.fehler;
    if (a.fehler.length) {
      this.assembliert = false;
      this.programm = [...this.start];
      this.note = a.fehler.length === 1 ? "Der Assembler hat einen Fehler gefunden." : `Der Assembler hat ${a.fehler.length} Fehler gefunden.`;
      this.render();
      return;
    }
    if (!a.zuordnung.length) {
      this.note = "Im Quelltext steht noch kein Befehl. Schreiben Sie einen Befehl pro Zeile, z. B. LOAD 14.";
      this.render();
      return;
    }
    this.busy = true;
    this.programm = [...this.start];
    this.render();
    const x0 = W - 6, xm = bitX(4) - 2; // der Editor steht rechts neben dem Speicher
    for (const z of a.zuordnung) {
      this.asmZeile = z.zeile;
      this.note = `Zeile ${z.zeile}: ${deute(z.wort)} → ${z.wort.slice(0, 4)} ${z.wort.slice(4)} → Zelle ${z.zelle}`;
      this.render();
      if (!reducedMotion()) {
        const y = rowY(z.zelle) + ROW_H / 2;
        await this.fly(z.wort, [[x0, y], [xm, y]], 420);
        if (this.destroyed) return;
      }
      this.programm[z.zelle] = z.wort;
      this.flash.add(`row:${z.zelle}`);
    }
    this.asmZeile = null;
    this.busy = false;
    this.assembliert = true;
    const n = a.zuordnung.length;
    this.note = `Assembliert: ${n} Befehl${n === 1 ? "" : "e"} in Zelle 0${n > 1 ? `–${n - 1}` : ""}. Jetzt „Schritt“ oder „Ablauf“.`;
    this.render();
  }

  private renderEditor(): void {
    if (!this.asm) return;
    const ta = this.root.querySelector<HTMLTextAreaElement>(".editor textarea")!;
    ta.disabled = this.busy || this.ablauf;
    const btn = this.root.querySelector<HTMLButtonElement>(".asm-btn")!;
    btn.disabled = this.busy || this.ablauf;
    btn.classList.toggle("primary", !this.assembliert);
    const zellen = new Map(assemble(this.task, this.quelltext).zuordnung.map(z => [z.zeile, z.zelle]));
    const fehlerZeilen = new Set(this.asmFehler.map(f => f.zeile));
    const zeilen = this.quelltext.split("\n");
    // Adressspalte: in welche Zelle kommt diese Zeile? (Fehlerzeilen belegen trotzdem eine Zelle)
    let zelle = 0;
    const gutter = zeilen.map((l, k) => {
      const n = k + 1;
      if (!l.split(";", 1)[0].trim()) return " ";
      const nr = zellen.get(n) ?? zelle;
      zelle = nr + 1;
      const cls = fehlerZeilen.has(n) ? "err" : n === this.asmZeile ? "akt" : "";
      return `<span class="${cls}">${fehlerZeilen.has(n) ? "⚠" : nr < ZELLEN ? `${adr(nr)} ${String(nr).padStart(2, " ")}` : ""}</span>`;
    });
    const g = this.root.querySelector<HTMLElement>(".editor .gutter")!;
    g.innerHTML = gutter.join("\n") + "\n";
    g.scrollTop = ta.scrollTop;
    this.root.querySelector(".asm-fehler")!.innerHTML = this.asmFehler.map(f => `<li>${esc(f.message)}</li>`).join("");
  }

  // ---------------------------------------------------------------- Zeiger: Tippen und Ziehen

  private pt(e: PointerEvent): [number, number] {
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(this.svg.getScreenCTM()!.inverse());
    return [p.x, p.y];
  }

  private rowAt(x: number, y: number): number | null {
    if (x < MEM_X0 || x > MEM_X1) return null;
    const i = Math.floor((y - rowY(0)) / ROW_H);
    return i >= 0 && i < ZELLEN ? i : null;
  }

  private down(e: PointerEvent): void {
    if (this.busy || e.button > 0 || this.asm) return; // im Assembler entsteht der Speicher nur aus dem Text
    const t = e.target as Element;
    this.tapZiel = t;
    const [x, y] = this.pt(e);
    const tile = t.closest<SVGGElement>("[data-tile]")?.dataset.tile;
    const row = t.closest<SVGGElement>("[data-row]")?.dataset.row;
    if (tile !== undefined) this.drag = { quelle: { tile: this.tiles[Number(tile)] }, x0: x, y0: y, aktiv: false };
    else if (row !== undefined && !this.istDaten(Number(row)) && this.programm[Number(row)] !== LEER) {
      this.drag = { quelle: { row: Number(row) }, x0: x, y0: y, aktiv: false };
    } else return;
    this.svg.setPointerCapture(e.pointerId);
  }

  private move(e: PointerEvent): void {
    const d = this.drag;
    if (!d) return;
    const [x, y] = this.pt(e);
    if (!d.aktiv && Math.hypot(x - d.x0, y - d.y0) < 8) return;
    if (!d.aktiv) {
      d.aktiv = true;
      const w = "tile" in d.quelle ? d.quelle.tile : this.programm[d.quelle.row];
      d.ghost = document.createElementNS("http://www.w3.org/2000/svg", "g");
      d.ghost.setAttribute("class", "ghost");
      d.ghost.innerHTML = this.tileSvg(w, -PAL_W / 2, -TILE_H / 2 + 4);
      this.flyLayer.appendChild(d.ghost);
      if ("tile" in d.quelle) this.svg.classList.add("ziehen"); // mögliche Ablagezellen zeigen
    }
    d.ghost!.setAttribute("transform", `translate(${x} ${y})`);
    const ziel = this.rowAt(x, y);
    this.svg.querySelectorAll(".fach.drop").forEach(el => el.classList.remove("drop"));
    if (ziel !== null && !this.istDaten(ziel)) this.svg.querySelector(`[data-row="${ziel}"]`)?.classList.add("drop");
    this.svg.querySelector(".palette-box")?.classList.toggle("trash-hot", "row" in d.quelle && x > MEM_X1);
  }

  private up(e: PointerEvent): void {
    const d = this.drag;
    const [x, y] = this.pt(e);
    this.endDrag();
    const t = this.tapZiel ?? (e.target as Element);
    this.tapZiel = null;
    // Schritt/Ablauf-Knöpfe in der CPU (vor dem busy-Test: „Anhalten“ muss auch während eines Schritts gehen)
    const act = t.closest<SVGElement>("[data-act]")?.dataset.act;
    if (act && !d?.aktiv) {
      if (!t.closest(".cpu-btn.disabled")) this.action(act);
      return;
    }
    if (d?.aktiv) {
      const ziel = this.rowAt(x, y);
      if ("tile" in d.quelle) {
        if (ziel !== null) { this.sel = ziel; this.adresseOffen = false; this.setzen(ziel, d.quelle.tile); }
      } else {
        const von = d.quelle.row, w = this.programm[von];
        if (x > MEM_X1) this.aendern(p => { p[von] = LEER; });           // zurück in die Palette = löschen
        else if (ziel !== null && ziel !== von && !this.istDaten(ziel)) {
          this.aendern(p => { p[von] = p[ziel]; p[ziel] = w; });          // tauschen (leere Zelle = verschieben)
          this.sel = ziel;
          this.adresseOffen = false;
        }
        this.render();
      }
      return;
    }
    if (this.busy) return;
    // Tippen
    const bzZelle = t.closest<SVGElement>("[data-bz]")?.dataset.bz;
    if (bzZelle !== undefined) { this.bzSetzen(Number(bzZelle)); return; }
    const tile = t.closest<SVGGElement>("[data-tile]")?.dataset.tile;
    if (tile !== undefined) {
      // Nach einem angetippten LOAD/STORE blieb die Auswahl für die Adresse stehen -- der nächste
      // Baustein gehört in die folgende Zelle (zum Ersetzen erst die Zelle antippen)
      if (this.adresseOffen && this.naechsteProgrammzelle(this.sel) !== this.sel) this.sel = this.naechsteProgrammzelle(this.sel);
      this.adresseOffen = false;
      const i = this.sel;
      if (this.istDaten(i)) return;
      const w = this.tiles[Number(tile)];
      this.setzen(i, w);
      // LOAD/STORE: Auswahl bleibt stehen, damit man gleich die Adressbits antippen kann
      if (BEFEHLE[w.slice(0, 4)].adresse) {
        this.adresseOffen = true;
        this.note = `Jetzt die Adresse einstellen: die rechten 4 Bit in Zelle ${i} antippen.`;
      } else this.sel = this.naechsteProgrammzelle(i);
      this.render();
      return;
    }
    const bit = t.closest<SVGGElement>("[data-bit]")?.dataset.bit;
    const row = t.closest<SVGGElement>("[data-row]")?.dataset.row;
    if (row === undefined) return;
    const i = Number(row);
    if (bit !== undefined && i === this.sel) {
      this.bitUmschalten(i, Number(bit));
      return;
    }
    if (this.istDaten(i)) {
      const d = this.daten.get(i)!;
      this.note = `Zelle ${i}${d.name ? ` (${d.name})` : ""} enthält Daten und ist gesperrt.`;
    } else {
      this.sel = i;
      this.adresseOffen = false;
      this.note = "";
    }
    this.render();
  }

  private endDrag(): void {
    this.drag?.ghost?.remove();
    this.drag = null;
    this.svg.querySelectorAll(".fach.drop").forEach(el => el.classList.remove("drop"));
    this.svg.classList.remove("ziehen");
    this.svg.querySelector(".palette-box")?.classList.remove("trash-hot");
  }

  // ---------------------------------------------------------------- Knöpfe

  private action(act: string): void {
    if (act === "ablauf" && this.ablauf) { this.ablauf = false; this.render(); return; }
    if (this.busy) return;
    switch (act) {
      case "assemble": void this.assemblieren(); return;
      case "schritt": void this.schritt(); return;
      case "ablauf": void this.ablaufen(); return;
      case "reset": this.zuruecksetzen(); this.render(); return;
      case "undo": {
        const prev = this.undoStack.pop();
        if (!prev) return;
        this.programm = prev;
        store.saveProgramm(this.task.id, this.programm);
        this.zuruecksetzen();
        this.render();
        return;
      }
      case "leeren": this.aendern(p => { p[this.sel] = LEER; }); this.render(); return;
      case "neu": this.neuerQuelltext(); return;
      case "clear": this.aendern(p => p.forEach((_, i) => { if (!this.istDaten(i)) p[i] = LEER; })); this.sel = 0; this.adresseOffen = false; this.render(); return;
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
    }
  }

  private zuruecksetzen(): void {
    this.lauf = null;
    this.bzVonHand = false;
    this.phase = "";
    this.br = null;
    this.bzAnzeige = null;
    this.aluText = "";
    this.result = null;
    this.solvedShown = false;
    this.note = "";
  }

  /** Befehlszähler von Hand setzen: Der nächste Schritt holt den Befehl aus Zelle i.
   *  Speicher und Register bleiben, wie sie sind -- auch nach HALT geht es so weiter. */
  private bzSetzen(i: number): void {
    if (this.asm && !this.assembliert) {
      this.note = "Erst „Assemblieren“: Die CPU versteht nur Bitmuster, keinen Text.";
      this.render();
      return;
    }
    if (!this.lauf) this.lauf = { zustand: anfang(this.programm), ereignisse: [] };
    this.lauf.zustand = { ...this.lauf.zustand, bz: i, halt: false };
    this.bzVonHand = true;
    this.result = null;
    this.note = `Befehlszähler von Hand auf ${i} gesetzt: Der nächste Schritt holt den Befehl aus Zelle ${i}.`;
    this.render();
  }

  // ---------------------------------------------------------------- Ausführen

  private async ablaufen(): Promise<void> {
    this.ablauf = true;
    if (this.lauf?.zustand.halt) this.zuruecksetzen();
    this.render();
    while (this.ablauf && !this.destroyed) {
      await this.schritt(true);
      if (!this.lauf || this.lauf.zustand.halt || this.lauf.ereignisse.length >= MAX_SCHRITTE) break;
    }
    this.ablauf = false;
    if (!this.destroyed) this.render();
  }

  /** Ein Befehl mit Animation: Holen → Entschlüsseln → Ausführen. */
  private async schritt(schnell = false): Promise<void> {
    if (this.busy) return;
    if (this.lauf?.zustand.halt) {
      this.note = "Die CPU hat angehalten. „Zurücksetzen“ startet wieder bei Zelle 0.";
      this.render();
      return;
    }
    if (this.lauf && this.lauf.ereignisse.length >= MAX_SCHRITTE) return;
    if (this.asm && !this.assembliert) {
      this.ablauf = false;
      this.note = "Erst „Assemblieren“: Die CPU versteht nur Bitmuster, keinen Text.";
      this.render();
      return;
    }
    if (!this.lauf && this.programm.every((w, i) => this.istDaten(i) || w === LEER)) {
      this.ablauf = false;
      this.note = "Der Speicher enthält noch keinen Befehl. Ziehen Sie zuerst Befehle in die Zellen ab Zelle 0.";
      this.render();
      return;
    }
    if (!this.lauf) this.lauf = { zustand: anfang(this.programm), ereignisse: [] };
    const z = this.lauf.zustand;
    const [neu, ev] = schritt(z);
    const animiert = !reducedMotion();
    const ms = schnell ? 450 : 800;
    this.busy = true;
    this.note = "";
    this.result = null;

    // Holen: Befehlszähler zeigt auf die Zelle, ihr Inhalt fliegt ins Befehlsregister
    this.phase = "holen";
    this.render();
    if (animiert) {
      await this.fly(z.speicher[z.bz], busWeg(rowY(z.bz) + ROW_H / 2, BR_Y), ms);
      if (this.destroyed) return;
    }
    this.br = z.speicher[z.bz];
    if (!ev.typ.match(/halt|unbekannt/)) this.bzAnzeige = neu.bz;
    this.flash.add("br");

    // Entschlüsseln: Steuerwerk liest den Befehlscode
    this.phase = "entschluesseln";
    this.render();
    if (animiert) await wait(schnell ? 350 : 700);
    if (this.destroyed) return;

    // Ausführen
    this.phase = "ausfuehren";
    this.render();
    if (animiert) {
      if (ev.typ === "load" || ev.typ === "store") {
        const a = dez(ev.adresse), y = rowY(a) + ROW_H / 2;
        await this.fly(ev.adresse, busWeg(y, BR_Y, BR_X1 - 2 * BIT_W).reverse(), ms * 0.7);
        if (this.destroyed) return;
        this.flash.add(`sel:${a}`);
        this.render();
        const zumReg = busWeg(y, REG_Y);
        await this.fly(ev.wert, ev.typ === "load" ? zumReg : [...zumReg].reverse(), ms);
        if (this.destroyed) return;
      } else if (ev.typ === "inc" || ev.typ === "shl") {
        this.aluText = ev.typ === "inc" ? "+1" : "×2";
        this.render();
        await wait(schnell ? 300 : 600);
        if (this.destroyed) return;
      }
    }
    this.lauf.zustand = neu;
    this.lauf.ereignisse.push(ev);
    this.bzAnzeige = null;
    if (ev.typ === "load" || ev.typ.match(/inc|shl/)) this.flash.add("reg");
    if (ev.typ === "store") this.flash.add(`row:${dez(ev.adresse)}`);
    this.note = beschreibe(ev);
    if (animiert) {
      this.render();
      await wait(schnell ? 200 : 350);
      if (this.destroyed) return;
    }
    this.phase = "";
    this.aluText = "";
    this.busy = false;
    if (neu.halt || this.lauf.ereignisse.length >= MAX_SCHRITTE) this.auswerten();
    else this.render();
  }

  private fly(bits: string, pts: [number, number][], ms: number): Promise<void> {
    const ns = "http://www.w3.org/2000/svg";
    const g = document.createElementNS(ns, "g");
    const w = bits.length * 11 + 18;
    g.setAttribute("class", "paket");
    g.innerHTML = `<rect x="${-w / 2}" y="-13" width="${w}" height="26" rx="8"/><text dy="5">${bits}</text>`;
    this.flyLayer.appendChild(g);
    const segs = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]));
    const len = segs.reduce((s, x) => s + x, 0) || 1;
    return new Promise(resolve => {
      const t0 = performance.now();
      const frame = (now: number) => {
        const f = Math.min(1, (now - t0) / ms);
        let d = (f < .5 ? 2 * f * f : 1 - (-2 * f + 2) ** 2 / 2) * len, k = 0; // sanft an- und abbremsen
        while (k < segs.length - 1 && d > segs[k]) { d -= segs[k]; k++; }
        const [x0, y0] = pts[k], [x1, y1] = pts[k + 1];
        const t = segs[k] ? Math.min(1, d / segs[k]) : 1;
        g.setAttribute("transform", `translate(${x0 + (x1 - x0) * t},${y0 + (y1 - y0) * t})`);
        if (f < 1 && !this.destroyed) requestAnimationFrame(frame);
        else { g.remove(); resolve(); }
      };
      requestAnimationFrame(frame);
    });
  }

  // ---------------------------------------------------------------- Prüfen

  private stars(r: Result): number {
    return 1 + (r.par_erreicht ? 1 : 0) + (r.par_erreicht && this.hintsShown === 0 ? 1 : 0);
  }

  /** Nach dem Ablauf bis HALT: den ganzen Lauf mit dem Prüfer nachrechnen (wie der Server). */
  private auswerten(): void {
    if (this.bzVonHand) {
      // der Prüfer rechnet ab Zelle 0 -- das wäre nicht der Lauf, den man gerade gesehen hat
      this.note = "Die CPU hat angehalten. Der Befehlszähler wurde von Hand gesetzt, deshalb wird dieser Lauf "
        + "nicht bewertet. „Zurücksetzen“ und „Ablauf“ prüfen das Programm ab Zelle 0.";
      this.render();
      return;
    }
    const loesung: Loesung = this.asm ? { quelltext: this.quelltext } : { speicher: [...this.programm] };
    const r = check(this.task, loesung);
    this.result = r;
    const detail = { loesung, hints: this.hintsShown };
    this.root.dispatchEvent(new CustomEvent("checked", { detail }));
    if (r.solved && !this.solvedShown) {
      this.solvedShown = true;
      const stars = this.stars(r);
      const before = store.stars();
      store.saveStars(this.task.id, stars);
      this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before } }));
      this.popStars = true;
    }
    this.render();
    this.popStars = false;
  }

  // ---------------------------------------------------------------- Darstellung

  private render(): void {
    if (this.destroyed) return;
    this.svg.querySelector("g.layer")!.innerHTML = this.drawing();
    this.flash.clear();
    this.renderToolbar();
    this.renderSide();
    this.renderEditor();
  }

  private renderToolbar(): void {
    const q = (act: string) => this.root.querySelector<HTMLButtonElement>(`.toolbar [data-act="${act}"]`)!; // nicht die CPU-Knöpfe im SVG
    const halt = !!this.lauf?.zustand.halt;
    const acts = this.asm ? ["neu", "assemble", "reset", "schritt", "hint"] : ["undo", "leeren", "clear", "reset", "schritt", "hint"];
    for (const act of acts) q(act).disabled = this.busy || this.ablauf;
    if (!this.asm) {
      q("undo").disabled ||= this.undoStack.length === 0;
      q("leeren").disabled ||= this.istDaten(this.sel) || this.programm[this.sel] === LEER;
      q("clear").disabled ||= this.programm.every((w, i) => w === this.start[i]);
    } else q("neu").disabled ||= this.quelltext === (this.task.vorlage ?? "");
    const bereit = !this.asm || this.assembliert;
    q("reset").disabled ||= !this.lauf;
    q("schritt").disabled ||= halt || !bereit;
    const ab = q("ablauf");
    ab.innerHTML = this.ablauf ? `${ICON.pause}<span>Anhalten</span>` : `${ICON.play}<span>${halt ? "Nochmal" : "Ablauf"}</span>`;
    ab.disabled = (this.busy && !this.ablauf) || !bereit;
    ab.classList.toggle("primary", bereit);
    if (this.asm) q("assemble").classList.toggle("primary", !this.assembliert);
    const hint = q("hint");
    const left = this.task.hinweise.length - this.hintsShown;
    hint.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hint.disabled ||= left <= 0;
    hint.hidden = this.task.hinweise.length === 0;
  }

  private bits(w: string, x: number, y: number, h: number, extra = "", nibGap = NIB_GAP): string {
    return [...w].map((b, k) => {
      const bx = x + k * BIT_W + (k >= 4 ? nibGap : 0);
      return `<rect class="cell ${b === "1" ? "hi" : "lo"} ${extra}" x="${bx}" y="${y}" width="${BIT_W - 2}" height="${h}" rx="4"/>
        <text class="cell-val" x="${bx + (BIT_W - 2) / 2}" y="${y + h / 2 + 5}">${b}</text>`;
    }).join("");
  }

  private tileSvg(w: string, x: number, y: number): string {
    const b = BEFEHLE[w.slice(0, 4)];
    const info = BAUSTEIN[b.name];
    const code = b.adresse ? `${w.slice(0, 4)} aaaa` : `${w.slice(0, 4)} 0000`;
    const box = `<rect class="tile-box ${b.name.toLowerCase()}" x="${x}" y="${y}" width="${PAL_W}" height="${TILE_H - 8}" rx="10"/>
      <text class="tile-sym" x="${x + 26}" y="${y + 38}">${info.sym}</text>`;
    // Maschinencode: Bitmuster und Wirkung; der Name nur als Tooltip am Desktop
    if (!this.asm) return `<title>${info.name}${b.adresse ? " a" : ""} (${b.name}${b.adresse ? " a" : ""})</title>${box}
      <text class="tile-name mono" x="${x + 54}" y="${y + 27}">${code}</text>
      <text class="tile-wirk" x="${x + 54}" y="${y + 47}">${esc(info.wirkung)}</text>`;
    return `${box}
      <text class="tile-name" x="${x + 54}" y="${y + 22}">${info.name}${b.adresse ? " a" : ""}</text>
      <text class="tile-code" x="${x + 54}" y="${y + 41}">${b.name}${b.adresse ? " a" : ""}</text>
      <text class="tile-code" x="${x + 54}" y="${y + 56}">${code}</text>`;
  }

  private drawing(): string {
    const parts: string[] = [];
    const z = this.lauf?.zustand;
    const sp = z ? z.speicher : this.programm;
    const bz = this.bzAnzeige ?? z?.bz ?? 0;
    const holBz = this.phase === "holen" || this.phase === "entschluesseln" || this.phase === "ausfuehren" ? z!.bz : null;

    // Palette (nur Maschinencode), rechts neben dem Speicher
    if (!this.asm) parts.push(`<g class="palette-box"><rect class="pal" x="${PAL_X - 6}" y="${MEM_TOP}" width="${PAL_W + 12}" height="${BOX_B - MEM_TOP}" rx="14"/>
      <text class="box-title" x="${PAL_X + PAL_W / 2}" y="${MEM_TOP + 30}">Befehle</text>
      <text class="pal-hint" x="${PAL_X + PAL_W / 2}" y="${MEM_TOP + 50}">← in den Speicher ziehen</text>
      <text class="trash-text" x="${PAL_X + PAL_W / 2}" y="${BOX_B - 22}">hierher ziehen = löschen</text></g>`);
    if (!this.asm) this.tiles.forEach((w, k) => {
      parts.push(`<g class="tile" data-tile="${k}" role="button" aria-label="${BAUSTEIN[BEFEHLE[w.slice(0, 4)].name].wirkung}">
        ${this.tileSvg(w, PAL_X, MEM_TOP + 64 + k * TILE_H)}</g>`);
    });

    // CPU: Steuerwerk oben, Rechenwerk unten
    parts.push(`<rect class="box" x="${CPU_X0}" y="${MEM_TOP}" width="${CPU_X1 - CPU_X0}" height="${BOX_B - MEM_TOP}" rx="14"/>
      <text class="box-title" x="${(CPU_X0 + CPU_X1) / 2}" y="${MEM_TOP + 30}">CPU</text>
      <text class="unit-title" x="${CPU_X0 + 18}" y="${BZ_Y - 40}">Steuerwerk</text>
      <line class="cpu-split" x1="${CPU_X0 + 12}" x2="${CPU_X1 - 12}" y1="${REG_Y - 80}" y2="${REG_Y - 80}"/>
      <text class="unit-title" x="${CPU_X0 + 18}" y="${REG_Y - 52}">Rechenwerk</text>`);
    // „Schritt“ und „Ablauf“ auch oben in der CPU (die Werkzeugleiste ist weit unten), nur als Symbol
    const bereit = !this.asm || this.assembliert, halt = !!this.lauf?.zustand.halt;
    const knopf = (act: string, x: number, aus: boolean, titel: string, icon: string, cls = "") =>
      `<g class="cpu-btn ${cls}${aus ? " disabled" : ""}" data-act="${act}" role="button" aria-label="${titel}" aria-disabled="${aus}">
        <title>${titel}</title><rect x="${x}" y="${MEM_TOP + 12}" width="${CPU_BTN_W}" height="32" rx="8"/>
        <g transform="translate(${x + CPU_BTN_W / 2 - 8} ${MEM_TOP + 20})">${icon}</g></g>`;
    const xAb = CPU_X1 - 14 - CPU_BTN_W, xSchritt = xAb - 8 - CPU_BTN_W;
    parts.push(knopf("schritt", xSchritt, this.busy || this.ablauf || halt || !bereit, "Schritt: einen Befehl ausführen",
        `<path d="M2 1l9 7-9 7z"/><path class="strich" d="M14 1v14"/>`),
      knopf("ablauf", xAb, (this.busy && !this.ablauf) || !bereit, this.ablauf ? "Anhalten" : halt ? "Nochmal" : "Ablauf: Programm bis zum Ende ausführen",
        this.ablauf ? `<path class="strich" d="M4 1v14M12 1v14"/>` : `<path d="M3 0l12 8-12 8z"/>`, "primary"));
    const label = (y: number, a: string, b: string) => `<text class="reg-name" x="${CELL_X - 12}" y="${y - 2}">${a}</text>
      <text class="reg-sub" x="${CELL_X - 12}" y="${y + 14}">${b}</text>`;
    // Befehlszähler
    const bzW = adr(bz);
    parts.push(label(BZ_Y, "Befehlszähler", "BZ"),
      `<g class="cells">${this.bits(bzW, CELL_X, BZ_Y - 13, 26, "", 0)}</g>
       <text class="reg-dez" x="${CELL_X + 4 * BIT_W + 8}" y="${BZ_Y + 6}">= ${bz}</text>`);
    // Befehlsregister
    const br = this.br ?? "        ";
    const brCls = this.flash.has("br") ? "cells flash" : "cells";
    parts.push(label(BR_Y, "Befehlsregister", ""),
      `<g class="${brCls}${this.phase === "entschluesseln" ? " decode" : ""}">${this.br
        ? this.bits(br, CELL_X, BR_Y - 13, 26)
        : [...br].map((_, k) => `<rect class="cell unk" x="${CELL_X + k * BIT_W + (k >= 4 ? NIB_GAP : 0)}" y="${BR_Y - 13}" width="${BIT_W - 2}" height="26" rx="4"/>`).join("")}</g>
       <text class="nib-label" x="${CELL_X + 2 * BIT_W}" y="${BR_Y + 30}">Befehl</text>
       <text class="nib-label" x="${CELL_X + 6 * BIT_W + NIB_GAP}" y="${BR_Y + 30}">Adresse</text>`);
    // Decoder
    const dec = this.br && this.phase !== "holen" ? erklaere(this.br, this.asm) : this.lauf ? "" : "Noch kein Befehl geholt";
    parts.push(`<rect class="decoder${this.phase === "entschluesseln" ? " active" : ""}" x="${CPU_X0 + 18}" y="${DEC_Y - 4}" width="${CPU_X1 - CPU_X0 - 36}" height="30" rx="8"/>
      <text class="decoder-text" x="${(CPU_X0 + CPU_X1) / 2}" y="${DEC_Y + 16}">${esc(dec)}</text>`);
    // Phasen des Von-Neumann-Zyklus
    const pw = (CPU_X1 - CPU_X0 - 36 - 16) / 3;
    PHASEN.forEach(([p, name], k) => {
      const x = CPU_X0 + 18 + k * (pw + 8);
      parts.push(`<g class="phase${this.phase === p ? " active" : ""}">
        <rect x="${x}" y="${PHASE_Y}" width="${pw}" height="30" rx="15"/>
        <text x="${x + pw / 2}" y="${PHASE_Y + 20}">${name}</text></g>`);
    });
    parts.push(`<text class="zyklus-note" x="${(CPU_X0 + CPU_X1) / 2}" y="${PHASE_Y + 54}">Von-Neumann-Zyklus</text>`);
    // Register mit Rechenwerk
    const reg = z?.register ?? LEER;
    parts.push(label(REG_Y, "Register", "Akkumulator"),
      `<g class="cells${this.flash.has("reg") ? " flash" : ""}">${this.bits(reg, CELL_X, REG_Y - 13, 26)}</g>
       <text class="reg-dez" x="${CELL_X}" y="${REG_Y + 36}">= ${dez(reg)}</text>`);
    if (this.aluText) parts.push(`<g class="alu"><rect x="${CELL_X + 80}" y="${REG_Y + 20}" width="56" height="28" rx="8"/>
      <text x="${CELL_X + 108}" y="${REG_Y + 40}">${this.aluText}</text></g>`);

    // Bus zwischen CPU und Speicher, je eine Abzweigung zu beiden
    parts.push(`<path class="sysbus" d="M${BUS_X} ${rowY(0)} V${rowY(ZELLEN)} M${CPU_X1} ${CPU_ABZ_Y} H${BUS_X} M${BUS_X} ${MEM_ABZ_Y} H${MEM_X0}"/>
      <text class="bus-label" transform="translate(${BUS_X - 10} ${rowY(ZELLEN) - 40}) rotate(-90)">Bus</text>`);

    // Speicher
    parts.push(`<rect class="box" x="${MEM_X0}" y="${MEM_TOP}" width="${MEM_X1 - MEM_X0}" height="${BOX_B - MEM_TOP}" rx="14"/>
      <text class="box-title" x="${(MEM_X0 + MEM_X1) / 2}" y="${MEM_TOP + 30}">Speicher (Programm + Daten)</text>
      <text class="fach-head end" x="${ADR_END}" y="${rowY(0) - 8}">Adresse</text>
      <text class="fach-head" x="${BITS_X}" y="${rowY(0) - 8}">Inhalt</text>
      <text class="fach-head" x="${DEUT_X}" y="${rowY(0) - 8}">als Befehl</text>`);
    for (let i = 0; i < ZELLEN; i++) {
      const y = rowY(i), w = sp[i], d = this.daten.get(i);
      const istBz = holBz === null ? !!this.lauf && i === bz : i === holBz; // gerade ausgeführte Zelle
      const geaendert = !!z && w !== this.programm[i];
      const c = ["fach", d ? "daten" : "prog", !this.asm && !this.busy && !this.ablauf && i === this.sel ? "cursor" : "", istBz ? "bz" : "",
        geaendert ? "changed" : "", this.flash.has(`row:${i}`) ? "flash" : "", this.flash.has(`sel:${i}`) ? "sel" : ""].join(" ");
      const adrBits = !d && !this.asm && !this.busy && !this.ablauf && i === this.sel && BEFEHLE[w.slice(0, 4)]?.adresse;
      const deut = d
        ? `<tspan class="dname">${esc(d.name || "Daten")}</tspan> = ${dez(w)}`
        : w === LEER ? `<tspan class="leer">(${this.asm ? "HALT" : "■"})</tspan>` : esc(this.deutung(w) ?? "?");
      parts.push(`<g class="${c}" data-row="${i}">
        <rect x="${MEM_X0 + 8}" y="${y + 1}" width="${MEM_X1 - MEM_X0 - 16}" height="${ROW_H - 2}" rx="6"/>
        <text class="fach-label" x="${ADR_END}" y="${y + ROW_H / 2 + 5}"><tspan class="fach-adr">${adr(i)}</tspan> <tspan class="fach-dez">(${i})</tspan></text>
        <rect class="bz-ziel" data-bz="${i}" x="${MEM_X0 + 8}" y="${y + 1}" width="${BITS_X - MEM_X0 - 14}" height="${ROW_H - 2}" rx="6">
          <title>Befehlszähler auf Zelle ${i} setzen</title></rect>
        ${[...w].map((b, k) => `<g ${adrBits && k >= 4 ? `data-bit="${k}" class="adr-bit"` : ""}>
          <rect class="cell ${b === "1" ? "hi" : "lo"}" x="${bitX(k)}" y="${y + 6}" width="${BIT_W - 2}" height="${ROW_H - 12}" rx="4"/>
          <text class="cell-val" x="${bitX(k) + (BIT_W - 2) / 2}" y="${y + ROW_H / 2 + 5}">${b}</text></g>`).join("")}
        <text class="deutung" x="${DEUT_X}" y="${y + ROW_H / 2 + 5}">${deut}</text>
        ${d ? `<g class="lock" transform="translate(${MEM_X0 + 24} ${y + ROW_H / 2})"><circle r="8"/><path d="M-2.5 -0.5V-2.5A2.5 2.5 0 0 1 2.5 -2.5V-0.5"/><rect x="-3.5" y="-0.5" width="7" height="5" rx="1"/></g>` : ""}
      </g>`);
    }
    // Ablage-Pfeil: der nächste angetippte Baustein landet in der ausgewählten Zelle
    if (!this.asm && !this.busy && !this.ablauf && !this.istDaten(this.sel)) {
      const y = rowY(this.sel) + ROW_H / 2, x0 = MEM_X1 + 4, x1 = PAL_X - 10;
      parts.push(`<path class="ablage-pfeil" d="M${x1} ${y} H${x0 + 10} M${x0 + 12} ${y - 7} L${x0} ${y} L${x0 + 12} ${y + 7}"/>`);
    }
    // Befehlszähler-Zeiger
    if (this.lauf) {
      const y = rowY(bz) + ROW_H / 2; // zeigt, was im Befehlszähler steht (nach dem Holen schon die nächste Zelle)
      parts.push(`<g class="bz-zeiger"><path d="M${MEM_X0 + 12} ${y - 9} L${MEM_X0 + 26} ${y} L${MEM_X0 + 12} ${y + 9}Z"/>
        <text x="${MEM_X0 + 30}" y="${y + 5}">BZ</text></g>`);
    }
    return parts.join("");
  }

  /** Spalte „als Befehl“: im Assembler das Kürzel (LOAD 14), im Maschinencode Symbol und Adresse (⇐ 14). */
  private deutung(w: string): string | null {
    if (this.asm) return deute(w);
    const b = BEFEHLE[w.slice(0, 4)];
    return b ? `${BAUSTEIN[b.name].sym}${b.adresse ? ` ${dez(w.slice(4))}` : ""}` : null;
  }

  private zielText(): string {
    const sp = this.lauf?.zustand.speicher ?? this.programm;
    return this.task.ziel.map(g => {
      const d = this.daten.get(g.zelle)!;
      const soll = g.wert.toString(2).padStart(8, "0");
      const ok = !!this.lauf?.zustand.halt && sp[g.zelle] === soll;
      return `<li class="${ok ? "ok" : ""}"><span class="mark">${ok ? "✓" : "○"}</span>
        Zelle ${g.zelle}${d.name ? ` (${esc(d.name)})` : ""} enthält ${soll} = ${g.wert}</li>`;
    }).join("");
  }

  private renderSide(): void {
    const t = this.task, r = this.result;
    let feedback = "";
    if (r?.solved) {
      const stars = this.stars(r);
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Geschafft!</strong> Mit ${r.schritte} Befehl${r.schritte === 1 ? "" : "en"} (${this.asm ? "HALT" : "Anhalten"} mitgezählt).
        ${!r.par_erreicht ? `<br>Es geht auch mit ${t.par} Befehlen -- schaffen Sie das?` : ""}
        ${r.par_erreicht && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong>
        <ul>${r.problems.map(p => `<li>${esc(p.message)}</li>`).join("")}</ul></div>`;
    }

    const ev = this.lauf?.ereignisse ?? [];
    const rows = ev.map((e, i) => `<tr><td>${i + 1}</td><td class="mono">${e.bz}</td>
        <td class="mono">${esc(this.asm ? deute(e.befehl) ?? e.befehl : `${e.befehl.slice(0, 4)} ${e.befehl.slice(4)}`)}</td></tr>
      <tr class="event"><td></td><td colspan="2">${esc(beschreibe(e))}</td></tr>`).join("");
    const hints = t.hinweise.slice(0, this.hintsShown).map(h => `<li>${esc(h)}</li>`).join("");
    const tabelle = [...t.befehle, "HALT" as Befehl].map(b => `<tr><td class="mono">${CODES[b]}${this.asm ? ""
      : BEFEHLE[CODES[b]].adresse ? " aaaa" : " 0000"}</td>${this.asm ? `<td class="mono">${b}${BEFEHLE[CODES[b]].adresse ? " a" : ""}</td>`
      : ""}<td>${esc(BAUSTEIN[b].wirkung)}</td></tr>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <div class="ziel"><strong>Ziel</strong><ul>${this.zielText()}</ul></div>
      ${this.note && !r ? `<p class="note" role="status">${esc(this.note)}</p>` : ""}
      <table class="protokoll">
        <thead><tr><th>#</th><th>Zelle</th><th>Befehl</th></tr></thead>
        <tbody>${rows || `<tr class="empty"><td colspan="3">Noch nichts ausgeführt</td></tr>`}</tbody>
      </table>
      <table class="befehlstabelle">
        <thead><tr><th>Code</th>${this.asm ? "<th>Befehl</th>" : ""}<th>Wirkung</th></tr></thead>
        <tbody>${tabelle}</tbody>
      </table>
      ${this.asm
        ? `<p class="tip">Ein Befehl pro Zeile, z. B. LOAD 14 -- Adressen dezimal (0 bis 15), Kommentare nach „;“.
            „Assemblieren“ schlägt jedes Kürzel in der Tabelle nach und schreibt das Bitmuster in die nächste
            Speicherzelle, beginnend bei Zelle 0.</p>`
        : `<p class="tip">Ziehen Sie Befehle in die Speicherzellen -- oder tippen Sie eine Zelle an und dann einen
            Befehl. Bei Befehlen mit Adresse (aaaa) sind die rechten 4 Bit die Adresse: in der ausgewählten Zelle antippen.</p>`}
      <p class="tip">Die CPU beginnt bei Zelle 0. Ein Klick auf eine Adresse setzt den Befehlszähler von Hand
        (solche Läufe werden nicht bewertet). Eine leere Zelle (00000000) ${this.asm ? "ist HALT" : "hält die CPU an"}.</p>
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }
}
